// Bonding Curve Implementation
// Uses constant product formula: x * y = k
// Where x = SOL reserves, y = token reserves, k = constant

export interface BondingCurveState {
  solReserve: number;
  tokenReserve: number;
  totalSupply: number;
  k: number; // constant product
}

export const INITIAL_TOKEN_SUPPLY = 1_000_000_000; // 1 billion tokens
export const INITIAL_SOL_RESERVE = 1; // 1 SOL initial liquidity
export const TRADING_FEE = 0.003; // 0.3% fee

export function initializeBondingCurve(): BondingCurveState {
  const solReserve = INITIAL_SOL_RESERVE;
  const tokenReserve = INITIAL_TOKEN_SUPPLY;
  const k = solReserve * tokenReserve;
  
  return {
    solReserve,
    tokenReserve,
    totalSupply: INITIAL_TOKEN_SUPPLY,
    k,
  };
}

export function calculatePrice(state: BondingCurveState): number {
  // Price = SOL reserve / Token reserve
  return state.solReserve / state.tokenReserve;
}

export function calculateMarketCap(state: BondingCurveState, solPrice: number = 200): number {
  // Market cap = (Total supply - remaining tokens) * price * SOL price in USD
  const circulatingSupply = state.totalSupply - state.tokenReserve;
  const tokenPrice = calculatePrice(state);
  return circulatingSupply * tokenPrice * solPrice;
}

export function calculateBuyOutput(
  state: BondingCurveState,
  solAmount: number
): { tokensOut: number; newState: BondingCurveState; fee: number; priceImpact: number } {
  const fee = solAmount * TRADING_FEE;
  const solAmountAfterFee = solAmount - fee;
  
  // Using constant product formula: new_token_reserve = k / new_sol_reserve
  const newSolReserve = state.solReserve + solAmountAfterFee;
  const newTokenReserve = state.k / newSolReserve;
  const tokensOut = state.tokenReserve - newTokenReserve;
  
  // Calculate price impact
  const priceBeforeTrade = calculatePrice(state);
  const priceAfterTrade = newSolReserve / newTokenReserve;
  const priceImpact = ((priceAfterTrade - priceBeforeTrade) / priceBeforeTrade) * 100;
  
  return {
    tokensOut,
    newState: {
      ...state,
      solReserve: newSolReserve,
      tokenReserve: newTokenReserve,
    },
    fee,
    priceImpact,
  };
}

export function calculateSellOutput(
  state: BondingCurveState,
  tokenAmount: number
): { solOut: number; newState: BondingCurveState; fee: number; priceImpact: number } {
  // Using constant product formula: new_sol_reserve = k / new_token_reserve
  const newTokenReserve = state.tokenReserve + tokenAmount;
  const newSolReserve = state.k / newTokenReserve;
  const solOutBeforeFee = state.solReserve - newSolReserve;
  
  const fee = solOutBeforeFee * TRADING_FEE;
  const solOut = solOutBeforeFee - fee;
  
  // Calculate price impact
  const priceBeforeTrade = calculatePrice(state);
  const priceAfterTrade = newSolReserve / newTokenReserve;
  const priceImpact = ((priceBeforeTrade - priceAfterTrade) / priceBeforeTrade) * 100;
  
  return {
    solOut,
    newState: {
      ...state,
      solReserve: newSolReserve,
      tokenReserve: newTokenReserve,
    },
    fee,
    priceImpact,
  };
}

export function calculateSolForTokens(
  state: BondingCurveState,
  tokenAmount: number
): number {
  // How much SOL needed to buy X tokens
  const newTokenReserve = state.tokenReserve - tokenAmount;
  const newSolReserve = state.k / newTokenReserve;
  const solNeeded = newSolReserve - state.solReserve;
  return solNeeded / (1 - TRADING_FEE); // Add fee on top
}

export function formatNumber(num: number, decimals: number = 2): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(decimals)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(decimals)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(decimals)}K`;
  return num.toFixed(decimals);
}

export function formatUSD(num: number): string {
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(2)}`;
}
